
import React from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MemorialBiographyView } from "./MemorialBiographyView";
import { GalleryGrid } from "@/components/GalleryGrid";
import { CommunitySection } from "@/components/CommunitySection";
import { AboutSection } from "@/components/memorial/AboutSection";
import { InfoCards } from "@/components/memorial/InfoCards";
import { MonumentInfoTab } from "@/components/memorial/info/MonumentInfoTab";
import { Button } from "@/components/ui/button";
import { Media } from "@/components/media/types";
import { TabSection } from "./TabSection";
import { SectionHeader } from "./SectionHeader";

interface MemorialTabsProps {
  activeTab: string;
  handleTabChange: (value: string) => void;
  startSlideshow: () => void;
  galleryMedia: Media[];
  isLoading: boolean;
  onViewMedia?: (url: string, index: number) => void;
  memorialId?: string;
  selectedBiographyCategory: { id: string; name: string } | null;
  onBiographyCategorySelect: (categoryId: string, categoryName: string) => void;
  onBackToBiographyCategories: () => void;
  monumentCompanyId?: string | null;
}

export const MemorialTabs: React.FC<MemorialTabsProps> = ({
  activeTab,
  handleTabChange,
  startSlideshow,
  galleryMedia,
  isLoading,
  onViewMedia,
  memorialId,
  selectedBiographyCategory,
  onBiographyCategorySelect,
  onBackToBiographyCategories,
  monumentCompanyId,
}) => {
  return (
    <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-8">
      <TabsList className="w-full justify-center border-b bg-transparent p-0 space-x-2 md:space-x-4">
        <TabsTrigger 
          value="about" 
          className="text-sm md:text-base data-[state=active]:border-b-2 data-[state=active]:border-primary px-2 md:px-4 py-2 transition-all duration-300"
          type="button"
        >
          Bio
        </TabsTrigger>
        <TabsTrigger 
          value="biography" 
          className="text-sm md:text-base data-[state=active]:border-b-2 data-[state=active]:border-primary px-2 md:px-4 py-2 transition-all duration-300"
          type="button"
        >
          Life Stories
        </TabsTrigger>
        <TabsTrigger 
          value="gallery" 
          className="text-sm md:text-base data-[state=active]:border-b-2 data-[state=active]:border-primary px-2 md:px-4 py-2 transition-all duration-300"
          type="button"
        >
          Gallery
        </TabsTrigger>
        <TabsTrigger
          value="community"
          className="text-sm md:text-base data-[state=active]:border-b-2 data-[state=active]:border-primary px-2 md:px-4 py-2 transition-all duration-300"
          type="button"
        >
          Tributes
        </TabsTrigger>
        {monumentCompanyId && (
          <TabsTrigger
            value="info"
            className="text-sm md:text-base data-[state=active]:border-b-2 data-[state=active]:border-primary px-2 md:px-4 py-2 transition-all duration-300"
            type="button"
          >
            Info
          </TabsTrigger>
        )}
      </TabsList>

      <TabsContent value="biography" className="animate-fade-in">
        <TabSection>
          <SectionHeader 
            title="Life Stories" 
            subtitle="Explore the chapters and memories that shaped their life" 
          />
          {memorialId && (
            <MemorialBiographyView
              memorialId={memorialId}
              selectedCategory={selectedBiographyCategory}
              onCategorySelect={onBiographyCategorySelect}
              onBackToCategories={onBackToBiographyCategories}
            />
          )}
        </TabSection>
      </TabsContent>

      <TabsContent value="gallery" className="animate-fade-in">
        <TabSection>
          <SectionHeader 
            title="Gallery" 
            subtitle="Cherished memories captured in photos and videos" 
          />
          <div className="space-y-6">
            <Button 
              variant="outline" 
              className="w-full py-4 md:py-6 hover:bg-primary/5 transition-colors duration-300" 
              onClick={startSlideshow}
              disabled={galleryMedia.length === 0 || isLoading}
            >
              {isLoading ? "Loading..." : galleryMedia.length === 0 ? "No media available" : "Start Slideshow"}
            </Button>
            <GalleryGrid onViewMedia={onViewMedia} />
          </div>
        </TabSection>
      </TabsContent>

      <TabsContent value="community" className="animate-fade-in">
        <TabSection>
          <SectionHeader 
            title="Tributes" 
            subtitle="Share your memories and pay tribute" 
          />
          <CommunitySection />
        </TabSection>
      </TabsContent>

      <TabsContent value="about" className="animate-fade-in">
        <TabSection>
          <AboutSection />
          <InfoCards />
        </TabSection>
      </TabsContent>

      {monumentCompanyId && memorialId && (
        <TabsContent value="info" className="animate-fade-in">
          <MonumentInfoTab
            memorialId={memorialId}
            monumentCompanyId={monumentCompanyId}
          />
        </TabsContent>
      )}
    </Tabs>
  );
};
